import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/Seo";
import { ASSETS } from "@/data/site";

const BODY = [
  "Jeg har alltid tilhørt den siste gruppen.",
  "I over 20 år har jeg jobbet med ernæring, helse og menneskekroppen. Ikke bare fra et kontor, men gjennom å bygge selskaper, utvikle produkter, drive restaurant, produsere økologisk mat og jobbe tett med tusenvis av mennesker som ønsket å få livet og helsen tilbake.",
  "Gjennom årene har jeg drevet en profilert helsekostbutikk, utviklet og produsert økologiske matvarer, bygget en supermatfabrikk i India, levert produkter til dagligvare, drevet restaurantkonsept for Choice Hotels og coachet mennesker innen kosthold, livsstil og prestasjon i snart to tiår.",
  "Men etter hvert begynte jeg å stille spørsmål som få andre stilte. Hvorfor blir stadig flere syke, samtidig som vi vet mer enn noen gang om helse? Hvorfor behandler vi symptomene, men snakker så lite om årsakene? Hvorfor har vi gjort kroppen så komplisert når den i utgangspunktet er skapt for å regulere og reparere seg selv?",
  "Jeg begynte å lese forskningen ingen snakket om. Studere historien. Se sammenhenger mellom kosthold, miljøgifter, stress, søvn, bevegelse, tarmhelse, nervesystem og livsstil.",
];

const BODY2 = [
  "Derfor handler arbeidet mitt ikke om raske løsninger.",
  "Det handler om å lære mennesker hvordan de kan forstå kroppen sin, ta tilbake eierskapet til helsen sin og ta informerte valg i en verden full av motstridende råd.",
];

const BODY3 = [
  "Jeg er ikke redd for å utfordre etablerte sannheter når forskning, erfaring eller logikk peker i en annen retning. Det betyr ikke at jeg mener jeg har alle svarene. Det betyr at jeg mener spørsmålene er like viktige som svarene.",
  "Gjennom hele karrieren har jeg ofte sett sammenhenger før de har blitt allment akseptert. Ikke fordi jeg er opptatt av å være kontroversiell, men fordi jeg har brukt tusenvis av timer på å observere, lese, teste, lytte og lære.",
];

const BODY4 = [
  "Å hjelpe mennesker til å bli sterke. Ikke bare fysisk, men mentalt og emosjonelt. Å forstå kroppen sin. Tenke selv. Ta bevisste valg. Og oppdage hvor mye kraft som faktisk finnes i dem når de slutter å lete etter alle svarene utenfor seg selv.",
  "Jeg tror fremtidens helse handler mindre om å gi fra seg ansvaret – og mer om å bygge kunnskap, motstandskraft og tillit til egen kropp.",
];

const BODY5 = [
  "Når vi begynner å se kroppen som et sammenhengende system, i stedet for en samling enkeltstående symptomer, åpner det seg en helt ny måte å tenke helse på.",
  "Det er den reisen jeg ønsker å ta deg med på.",
  "Hvis du er her, er du sannsynligvis en av dem som ikke lenger nøyer deg med enkle forklaringer.",
];

function PullQuote({ children }) {
  return (
    <Reveal>
      <blockquote className="my-12 border-l-2 border-terra pl-8 font-serif italic text-2xl md:text-3xl leading-snug text-olive-deep">
        {children}
      </blockquote>
    </Reveal>
  );
}

export default function OmCaroline() {
  return (
    <div className="relative bg-cream text-olive-deep overflow-clip">
      <Nav />
      <Seo title="Om Caroline – Helserebellen" description="Møt Caroline, grunnleggeren av Helserebellen. Snart 20 år i helse- og ernæringsbransjen – kunnskap, erfaring og helse på egne premisser." path="/om-caroline" />

      {/* header */}
      <section className="relative pt-[110px] md:pt-[130px] pb-10 px-5 md:px-10 overflow-hidden">
        <img src={ASSETS.botanicalRight} alt="" className="pointer-events-none absolute top-20 -right-10 w-72 rotate-[18deg] blend-multiply opacity-80 hidden lg:block" />
        <img src={ASSETS.botanicalLeft} alt="" className="pointer-events-none absolute top-40 -left-12 w-56 -rotate-12 blend-multiply opacity-70 hidden lg:block" />
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Link to="/" className="inline-flex items-center gap-2 text-olive/60 text-sm hover:text-terra mb-8"><ArrowLeft size={16} /> Tilbake</Link>
            <p className="text-[12px] font-semibold uppercase tracking-[0.34em] text-terra">Om Caroline</p>
            <h1 className="mt-5 font-serif text-4xl md:text-6xl font-medium leading-[1.02] text-olive-deep text-balance">
              Noen mennesker følger oppskriften. Andre spør hvorfor oppskriften eksisterer i utgangspunktet.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* portrait */}
      <section className="px-5 md:px-10">
        <Reveal className="mx-auto max-w-4xl">
          <div className="rounded-2xl overflow-hidden aspect-[16/10] shadow-xl">
            <img src={ASSETS.caroline} alt="Caroline" className="w-full h-full object-cover object-[center_25%]" />
          </div>
        </Reveal>
      </section>

      {/* long-form story */}
      <article className="mx-auto max-w-2xl px-5 md:px-0 py-14 md:py-20 text-lg leading-[1.75] text-olive/85">
        {BODY.map((p, i) => (
          <Reveal key={i} delay={i * 0.02}><p className="mb-6">{p}</p></Reveal>
        ))}

        <PullQuote>
          Og én ting ble stadig tydeligere: Kroppen fungerer som et intelligent system. Når vi fjerner belastninger
          og gir den det den faktisk trenger, har den en bemerkelsesverdig evne til å bevege seg tilbake mot balanse.
        </PullQuote>

        {BODY2.map((p, i) => (
          <Reveal key={i}><p className="mb-6">{p}</p></Reveal>
        ))}

        <PullQuote>Jeg følger ikke trender, jeg følger nysgjerrigheten.</PullQuote>

        {BODY3.map((p, i) => (
          <Reveal key={i}><p className="mb-6">{p}</p></Reveal>
        ))}

        <Reveal><p className="mb-6 font-serif text-2xl text-olive-deep">Mitt mål er enkelt:</p></Reveal>
        {BODY4.map((p, i) => (
          <Reveal key={i}><p className="mb-6">{p}</p></Reveal>
        ))}

        <PullQuote>
          Det som driver meg mest, er ikke ønsket om å ha rett. Det er ønsket om å forstå hele mennesket.
        </PullQuote>

        {BODY5.map((p, i) => (
          <Reveal key={i}><p className="mb-6">{p}</p></Reveal>
        ))}

        <Reveal>
          <p className="mb-10 font-serif italic text-2xl text-terra">Da tror jeg du har kommet til riktig sted. ♥</p>
        </Reveal>

        <Reveal>
          <p className="font-script text-4xl text-olive-deep">With love, Caroline</p>
        </Reveal>
      </article>

      {/* CTA */}
      <section className="bg-[#4A5138] text-cream py-16 px-5 text-center">
        <Reveal>
          <p className="font-script text-3xl text-sage">Klar for å ta tilbake helsen?</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Utforsk planene og kom i gang.</h2>
          <Link to="/#velg" className="mt-8 inline-flex items-center gap-2 rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-cream hover:text-olive-deep transition-colors" data-testid="omcaroline-cta">
            Utforsk planene <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <div className="bg-terra text-cream text-center py-6 px-5">
        <p className="font-script text-2xl text-cream/90">La oss gjøre det – sammen.</p>
      </div>
    </div>
  );
}
